import { useSyncExternalStore } from 'react';

// The public status page (TF-133, docs/adr/0125-status-page.md): UptimeRobot's, on its own address, so it
// answers when DoCuLat does not. Linked where it is needed: the "cannot be reached" banner, the error
// page, under the sign-in page and in every legal footer. The same address as the marketing site's.

export const STATUS_PAGE_URL = 'https://status.doculat.eu';

// Whether the platform answers, per service (the path's /api/<service>/): set by every request the app
// makes (api/http.ts). A request that never reached it - no network, or the gateway saying the service
// behind it is down - marks that service unreachable; its next answer of any kind, or two minutes without
// another failure, clears it. Per service, so one service down is not hidden by the others answering.
const FORGET_MS = 120_000;
const failing = new Map<string, number>();
const listeners = new Set<() => void>();
let unreachable = false;

function changed(): void {
  const now = failing.size > 0;
  if (now === unreachable) return;
  unreachable = now;
  for (const listener of listeners) listener();
}

/** The service a path belongs to: "crm" for /api/crm/v1/..., the path itself otherwise. */
export const serviceOf = (path: string) => /^\/api\/([^/]+)\//.exec(path)?.[1] ?? path;

export function reportReachable(path: string, reached: boolean): void {
  const service = serviceOf(path);
  window.clearTimeout(failing.get(service));
  if (reached) failing.delete(service);
  else
    failing.set(
      service,
      window.setTimeout(() => {
        failing.delete(service);
        changed();
      }, FORGET_MS),
    );
  changed();
}

/** True while a service's last request did not reach the platform. */
export function useUnreachable(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => unreachable,
  );
}
